import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  IconButton,
  Grid,
  Alert,
  Chip
} from '@mui/material';
import { Edit as EditIcon, Info as InfoIcon } from '@mui/icons-material';
import activityService from '../services/activityService';
import { useAuth } from '../contexts/auth';

const Atividades = () => {
  // Estados
  const { user, validateToken } = useAuth();
  const [atividades, setAtividades] = useState([]);
  const [filters, setFilters] = useState({ name: '', description: '', active: '' });
  const [openDialog, setOpenDialog] = useState(false);
  const [currentActivity, setCurrentActivity] = useState({ name: '', description: '', active: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Verificar se o usuário tem lares vinculados
  const hasLinkedHomes = user?.profile === 'Administrador' || (user?.laresVinculados && user.laresVinculados.length > 0);

  // Mensagem informativa com base no perfil do usuário
  const getNoHomesMessage = () => {
    if (user?.profile === 'Gestor') {
      return 'Você não possui lares vinculados. Como gestor, você precisa estar vinculado a pelo menos um lar para gerenciar atividades. Entre em contato com um administrador para vincular você a um ou mais lares.';
    } else if (user?.profile !== 'Administrador') {
      return 'Você não possui lares vinculados. Entre em contato com um administrador para vincular você a um ou mais lares.';
    }
    return '';
  };

  // Atualizar informações do usuário ao carregar a página
  useEffect(() => {
    const updateUserInfo = async () => {
      try {
        // Validar o token e atualizar as informações do usuário
        await validateToken();
        console.log('Informações do usuário atualizadas na página de Atividades');
      } catch (error) {
        console.error('Erro ao atualizar informações do usuário:', error);
      }
    };
    
    updateUserInfo();
  }, [validateToken]);

  // Carregar atividades
  const loadAtividades = useCallback(async () => {
    try {
      const data = await activityService.list(filters);
      setAtividades(data);
    } catch (err) {
      setError('Erro ao carregar atividades: ' + err.message);
    }
  }, [filters]);

  // Carregar atividades ao montar o componente
  useEffect(() => {
    loadAtividades();
  }, [loadAtividades]);

  // Recarregar quando os filtros mudarem
  useEffect(() => {
    loadAtividades();
  }, [filters, loadAtividades]);

  // Recarregar quando o diálogo for fechado
  useEffect(() => {
    if (!openDialog) {
      loadAtividades();
    }
  }, [openDialog, loadAtividades]);

  // Aplicar filtros
  const handleFilter = () => {
    loadAtividades();
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({ name: '', description: '', active: '' });
    loadAtividades();
  };

  // Abrir diálogo para criar/editar
  const handleOpenDialog = (activity = null) => {
    if (activity) {
      setCurrentActivity(activity);
    } else {
      setCurrentActivity({ name: '', description: '', active: true });
    }
    setOpenDialog(true);
  };

  // Fechar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentActivity({ name: '', description: '', active: true });
    setError('');
  };

  // Salvar atividade
  const handleSave = async () => {
    try {
      if (!currentActivity.name.trim()) {
        setError('O nome da atividade é obrigatório');
        return;
      }

      if (currentActivity.id) {
        await activityService.update(currentActivity.id, currentActivity);
        setSuccess('Atividade atualizada com sucesso!');
      } else {
        await activityService.create(currentActivity);
        setSuccess('Atividade criada com sucesso!');
      }

      handleCloseDialog();
      loadAtividades();
    } catch (err) {
      setError('Erro ao salvar atividade: ' + err.message);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Atividades</Typography>

      {/* Mensagens de erro e sucesso */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Mensagem para usuários sem lares vinculados */}
      {!hasLinkedHomes && user?.profile !== 'Administrador' && (
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ mb: 2 }}
        >
          {getNoHomesMessage()}
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Nome"
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Descrição"
              value={filters.description}
              onChange={(e) => setFilters({ ...filters, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              select
              label=""
              value={filters.active}
              onChange={(e) => setFilters({ ...filters, active: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="">Todos</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="contained" onClick={handleFilter} sx={{ mr: 1 }}>
              Filtrar
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Botão Adicionar */}
      <Button
        variant="contained"
        color="primary"
        onClick={() => handleOpenDialog()}
        sx={{ mb: 2 }}
        disabled={!hasLinkedHomes && user?.profile !== 'Administrador'}
      >
        Nova Atividade
      </Button>

      {/* Tabela de Atividades */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {atividades.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {hasLinkedHomes ? 'Nenhuma atividade encontrada.' : 'Você não tem acesso a nenhuma atividade.'}
                </TableCell>
              </TableRow>
            ) : (
              atividades.map((atividade) => (
                <TableRow key={atividade.id}>
                  <TableCell>{atividade.name}</TableCell>
                  <TableCell>{atividade.description}</TableCell>
                  <TableCell>
                    <Chip 
                      label={atividade.active ? 'Ativo' : 'Inativo'}
                      color={atividade.active ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenDialog(atividade)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de Criar/Editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentActivity.id ? 'Editar Atividade' : 'Nova Atividade'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nome"
              value={currentActivity.name}
              onChange={(e) => setCurrentActivity({ ...currentActivity, name: e.target.value })}
              error={!!error && !currentActivity.name.trim()}
              helperText={error && !currentActivity.name.trim() ? error : ''}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Descrição"
              value={currentActivity.description || ''}
              onChange={(e) => setCurrentActivity({ ...currentActivity, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            {currentActivity.id && (
              <FormControlLabel
                control={
                  <Switch
                    checked={currentActivity.active}
                    onChange={(e) => setCurrentActivity({ ...currentActivity, active: e.target.checked })}
                  />
                }
                label={currentActivity.active ? 'Ativo' : 'Inativo'}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Atividades;
